import React, {FC, RefObject, useRef} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateLicenseTypesEnum} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {DataEventTrackingProvider} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {
  SettingsInboxesResourcePagesEnum,
  SettingsInboxesTabPagesEnum,
} from '../../../../models/url/models/settingsInboxesUrlModel';
import {SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {PermissionSimpleDropdownItem} from '../../../common/dropdowns/permissionSimpleDropdownItem';
import {TeamWorkspaceInfoBubble} from '../../../infoBubbles/teamWorkspaceInfoBubble';
import {SandboxDemoIntroEducationTip} from '../../../onboarding/admin/demos/sandboxDemoIntroEducationTip';
import {WelcomeEducationTipCoordinator} from '../../../onboarding/sandbox/educationTips/welcomeEducationTipCoordinator';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {WorkspaceHeaderMoreDropdownRenderProps} from '../header/workspaceHeaderMore';
import {Workspace} from '../workspace';
import {TeamWorkspaceCustomViewsContainer} from './teamWorkspaceCustomViewsContainer';
import {TeamWorkspaceInboxesContainer} from './teamWorkspaceInboxesContainer';

/*
 * Props.
 */

export interface TeamWorkspaceProps
  extends Pick<WorkspaceHeaderMoreDropdownRenderProps, 'hideWorkspaceLabel' | 'onHideWorkspace'> {
  panelId: InboxesPanelId;
  /** The team that this workspace is for. */
  team: TeamModel;
  /** Teams data passed from VisualWorkspaceModel */
  visualWorkspaceTeam: VisualWorkspaceTeamData;
  /** User license type (full). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether the user has permission to add an inbox to this team workspace. */
  hasAddInboxPermission: boolean;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Whether this is the first team. */
  isFirstTeam: boolean;
  /** Conversation id of the selected sandbox conversation in P2, if any. */
  onlySelectedSandboxConversationId: number | undefined;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: () => void;
  /** Whether inboxes cannot be dragged to reorder. */
  isNotReorderable: boolean;
  /** Only render custom views if true */
  hasCustomViews: boolean;
}

/*
 * Component.
 */

/** Display a workspace for the specified team. */
export const TeamWorkspace: FC<TeamWorkspaceProps> = (props) => {
  const {panelId, team, isExpanded, isFirstTeam, userLicenseType, onlySelectedSandboxConversationId} = props;
  const hasPermission = useAppSelector(getHasPermission);

  const teamWorkspaceRef = useRef<HTMLDivElement>(null);
  const {navigateToLink} = useLinkNavigation();
  const onCreateClick = () => {
    navigateToLink(
      buildSettingsPath({
        rootPage: SettingsRootPagesEnum.INBOXES,
        namespace: team.namespace,
        resourcePage: SettingsInboxesResourcePagesEnum.NEW,
        tabPage: SettingsInboxesTabPagesEnum.INBOXES,
      }),
    );
  };
  const createItem = hasPermission(team.namespace, PermissionsEnum.CREATE_INBOX) ? (
    <PermissionSimpleDropdownItem onSelect={onCreateClick}>
      <Trans id="lenses-workspace-create-inbox">Create inbox</Trans>
    </PermissionSimpleDropdownItem>
  ) : null;

  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  /*
   * Render.
   */

  // Render the content of the workspace.
  const renderContent = () => (
    <SandboxDemoIntroEducationTip isSandboxTeam={team.isSandbox}>
      <WelcomeEducationTipCoordinator
        conversationId={onlySelectedSandboxConversationId}
        isSandboxTeam={team.isSandbox}
      >
        {/* Header. */}
        <WorkspaceHeader
          panelId={panelId}
          interactionId="team"
          isExpanded={isExpanded}
          onToggleExpandClick={props.onToggleExpandClick}
          hideWorkspaceLabel={props.hideWorkspaceLabel}
          onHideWorkspace={props.onHideWorkspace}
          extraItems={createItem}
        >
          {team.name}
        </WorkspaceHeader>

        {/* Info bubble. */}
        {maybeRenderInfoBubble(isFirstTeam, teamWorkspaceRef)}

        {/* Workspace content. */}
        {maybeRenderExpandedContent(props)}
      </WelcomeEducationTipCoordinator>
    </SandboxDemoIntroEducationTip>
  );

  // Wrap in a workspace and return.
  return (
    <Workspace ref={isFirstTeam ? teamWorkspaceRef : undefined} isExpanded={isExpanded}>
      {renderContent()}
    </Workspace>
  );
};

function maybeRenderExpandedContent(props: TeamWorkspaceProps) {
  const {panelId, isExpanded, team, visualWorkspaceTeam, hasCustomViews} = props;
  if (!isExpanded) {
    return null;
  }

  return (
    <>
      <TeamWorkspaceInboxesContainer
        panelId={panelId}
        team={team}
        visualWorkspaceInboxesForTeam={visualWorkspaceTeam.inboxes}
        isNotReorderable={props.isNotReorderable}
      />
      {hasCustomViews && (
        <TeamWorkspaceCustomViewsContainer
          panelId={panelId}
          team={team}
          visualWorkspaceCustomViewsForTeam={visualWorkspaceTeam.customViews}
          isNotReorderable={props.isNotReorderable}
        />
      )}
    </>
  );
}

function maybeRenderInfoBubble(isFirstTeam: boolean, workspaceRef: RefObject<HTMLElement>) {
  if (!isFirstTeam) {
    return null;
  }

  return (
    <DataEventTrackingProvider context={{name: 'getting_started', payload: {}}}>
      <TeamWorkspaceInfoBubble anchor={workspaceRef} />
    </DataEventTrackingProvider>
  );
}
